import React from 'react';

import { useStoreContext } from 'features/store';
import { useIntl } from 'react-intl';
import { ActivityIndicator, StyleSheet, Text, View } from 'react-native';

import ActionButton, { ActionButtonVariants } from 'components/action-button';
import Dialog from 'components/dialog';
import { primitive } from 'styles/constants/primitives';

import { AvailableRestaurantDialogBody } from './components/available-restaurant-dialog-body';
import { DialogHeader } from './components/header';
import { UnavailableRestaurantDialogBody } from './components/unavailable-restaurant-dialog-body';
import { useStoreOrderUnavailableDialog } from './hooks/use-store-order-unavailable-dialog';
import { getConfirmButtonLabel, getDialogBody } from './utils';

/**
 * This component is used to display a dialog that allows the user to select a nearby restaurant.
 * It is used when the user's order cannot be completed at the current restaurant.
 * If the user doesn't have permission to access their location we will use the closed store's location to find a nearby restaurant.
 * Also if there is not an available restaurant we will display a message to the user to just go to the store locator to find a nearby restaurant.
 */

export const StoreOrderUnavailableDialog = React.memo(() => {
  const { formatMessage } = useIntl();
  const { isSelectingStore } = useStoreContext();

  const {
    isDialogOpen,
    selectNearbyStore,
    chooseAnotherStore,
    availableRestaurantForPickUp,
    isDelivery,
    isRestaurantSearchLoading,
  } = useStoreOrderUnavailableDialog();

  const confirmButtonLabel = getConfirmButtonLabel(
    isDelivery,
    !!availableRestaurantForPickUp?._id,
    formatMessage
  );

  const title = isDelivery
    ? formatMessage({ id: 'restaurantClosedDialogHeadingDelivery' })
    : formatMessage({ id: 'restaurantClosedDialogHeading' });

  const body = getDialogBody(isDelivery, !!availableRestaurantForPickUp?._id, formatMessage);

  const PickUpActions = () => {
    return (
      <>
        {availableRestaurantForPickUp?._id && (
          <ActionButton
            onPress={selectNearbyStore}
            variant={ActionButtonVariants.PRIMARY}
            fullWidth
            isLoading={isSelectingStore}
            disabled={isSelectingStore}
          >
            {confirmButtonLabel}
          </ActionButton>
        )}
        <ActionButton
          onPress={chooseAnotherStore}
          variant={
            availableRestaurantForPickUp?._id
              ? ActionButtonVariants.TEXT_ONLY
              : ActionButtonVariants.PRIMARY
          }
          fullWidth
        >
          {formatMessage({ id: 'restaurantClosedDialogButtonCancel' })}
        </ActionButton>
      </>
    );
  };

  const DeliveryActions = () => {
    return (
      <ActionButton
        isLoading={isSelectingStore}
        onPress={chooseAnotherStore}
        variant={ActionButtonVariants.PRIMARY}
        fullWidth
      >
        {confirmButtonLabel}
      </ActionButton>
    );
  };

  return (
    <Dialog
      modalAppearanceEventMessage="RestaurantClosedDialog"
      showDialog={isDialogOpen}
      onDismiss={chooseAnotherStore}
      headingComponent={<DialogHeader title={title} />}
      showCloseButton={false}
      actions={
        <View style={styles.actionsContainer}>
          {isDelivery ? <DeliveryActions /> : <PickUpActions />}
        </View>
      }
      bodyComponent={
        isRestaurantSearchLoading ? (
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="small" />
            <Text style={styles.loadingText}>
              {formatMessage({
                id: 'restaurantClosedDialogBodyLoading',
                defaultMessage: 'Searching for a nearby restaurant...',
              })}
            </Text>
          </View>
        ) : availableRestaurantForPickUp ? (
          <AvailableRestaurantDialogBody
            body={body}
            availableRestaurant={availableRestaurantForPickUp}
            formatMessage={formatMessage}
          />
        ) : (
          <UnavailableRestaurantDialogBody body={body} formatMessage={formatMessage} />
        )
      }
    />
  );
});

const styles = StyleSheet.create({
  actionsContainer: {
    marginTop: 12,
    flex: 1,
  },
  loadingContainer: {
    padding: 20,
    alignItems: 'center',
    justifyContent: 'center',
  },
  loadingText: {
    marginTop: 12,
    textAlign: 'center',
    fontFamily: primitive.fontFamily.$body,
  },
});
